import { useRef, useState } from 'react';
import { FieldErrors, useForm } from 'react-hook-form';
import { useGAFormTracking } from '../../../shared/hooks';
import { yupResolver } from '@hookform/resolvers/yup';
import FormField from '@ramsey-design-system/form-field';
import Input from '@ramsey-design-system/input';
import {
  getMatcherFormName,
  MATCHER_NUMERIC_INPUT_ATTRS,
  useMatcherFieldSize,
  useTrackMatcherStepStarted
} from '../utils';
import { matcherPhoneVerificationSchema } from '../schemas/MatcherPhoneVerificationSchema';
import { startMatcherVerification } from '../api';
import { useMatcherContext } from '../context';
import { getAnsweredString } from '../flow/types';
import { getTaxFullUrl, getVerificationBypass } from '../../../shared/utils';
import { MatcherVerificationStatus } from '../enums';
import {
  trackStepCompleted,
  trackStepErrorViewed
} from '../../../shared/analytics';
import { useWizard } from 'react-use-wizard';
import {
  MatcherStandardStepTitle,
  MatcherStandardStepSubtitleText,
  MatcherStepWrapper,
  MatcherSubmitButton,
  MatcherResubmitButton,
  MatcherErrorMessage,
  MatcherSuccessMessage
} from '../components';

const STEP_NAME = 'matcher_verification_step';
const STEP_LABEL = 'Enter verification code.';

interface MatcherVerificationStepFields {
  verification_code: string;
}

interface MatcherVerificationResponseBody {
  validated: boolean;
  verificationToken?: string;
}

function formatPhoneForDisplay(phone: string): string {
  const match = phone.match(/^(\d{3})(\d{3})(\d{4})$/);
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phone;
}

function MatcherVerificationStep() {
  const formRef = useGAFormTracking(STEP_NAME);
  const [status, setStatus] = useState<MatcherVerificationStatus>(
    MatcherVerificationStatus.initial
  );
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const {
    matcherAnswers,
    updatePhoneVerificationCountDown,
    updateVerificationToken
  } = useMatcherContext();
  const phone = getAnsweredString(matcherAnswers.phone);
  const postalCode = getAnsweredString(matcherAnswers.postalCode);
  const verificationType =
    getAnsweredString(matcherAnswers.verificationType) || 'sms';
  const { activeStep, nextStep } = useWizard();
  // Set synchronously, unlike `status`, which only lands after react-hook-form's async validation
  // resolves and a re-render. Holding Enter in the code input auto-repeats at ~30ms, which is
  // faster than that round trip on a slow device - the buttons are still enabled in that gap.
  const requestInFlight = useRef(false);
  const fieldSize = useMatcherFieldSize();
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<MatcherVerificationStepFields>({
    resolver: yupResolver(matcherPhoneVerificationSchema),
    defaultValues: { verification_code: '' }
  });

  useTrackMatcherStepStarted(activeStep + 1, STEP_NAME, STEP_LABEL);

  function handleResendCode() {
    if (requestInFlight.current) {
      return;
    }
    requestInFlight.current = true;
    setErrorMessage('');
    setSuccessMessage('');
    setStatus(MatcherVerificationStatus.resendingCode);

    startMatcherVerification(phone, verificationType, postalCode)
      .then(() => {
        setStatus(MatcherVerificationStatus.resendCodeSuccess);
        updatePhoneVerificationCountDown(30);
        setSuccessMessage('Code resend successful.');
      })
      .catch((e) => {
        const resendErrorMessage =
          "Sorry, we couldn't resend a code. Try again.";
        setStatus(MatcherVerificationStatus.initial);
        setErrorMessage(resendErrorMessage);
        trackStepErrorViewed({
          step: activeStep + 1,
          step_name: STEP_NAME,
          step_label: STEP_LABEL,
          error_type: 'system',
          error_message: resendErrorMessage,
          form_name: getMatcherFormName()
        });
        console.error(e);
      })
      .finally(() => {
        requestInFlight.current = false;
      });
  }

  function handleInvalidVerificationResponse() {
    const invalidCodeErrorMessage =
      "Sorry, we weren't able to verify your code. Try again.";
    setStatus(MatcherVerificationStatus.initial);
    setErrorMessage(invalidCodeErrorMessage);
    trackStepErrorViewed({
      step: activeStep + 1,
      step_name: STEP_NAME,
      step_label: STEP_LABEL,
      error_type: 'system',
      error_message: invalidCodeErrorMessage,
      form_name: getMatcherFormName()
    });
    console.warn('Twilio responded with incorrect verification code');
  }

  function handleVerificationResponse(
    responseBody: MatcherVerificationResponseBody
  ) {
    if (responseBody?.validated) {
      onValidSubmit(responseBody.verificationToken);
    } else {
      handleInvalidVerificationResponse();
    }
  }

  function onValidSubmit(verificationToken?: string) {
    trackStepCompleted({
      step: activeStep + 1,
      step_name: STEP_NAME,
      step_label: STEP_LABEL,
      answer_labels: [],
      answer_values: [],
      form_name: getMatcherFormName()
    });
    setStatus(MatcherVerificationStatus.redirecting);
    updateVerificationToken(verificationToken ?? '');
    nextStep();
  }

  async function onSubmit(values: MatcherVerificationStepFields) {
    if (requestInFlight.current) {
      return;
    }
    requestInFlight.current = true;
    setErrorMessage('');
    try {
      const response = await endVerification(phone, values.verification_code);
      handleVerificationResponse(response);
    } catch (e) {
      const networkErrorMessage =
        "Sorry, we weren't able to verify your code. Try again.";
      console.error(e);
      setErrorMessage(networkErrorMessage);
      trackStepErrorViewed({
        step: activeStep + 1,
        step_name: STEP_NAME,
        step_label: STEP_LABEL,
        error_type: 'system',
        error_message: networkErrorMessage,
        form_name: getMatcherFormName()
      });
      setStatus(MatcherVerificationStatus.initial);
    } finally {
      // By now `status` is either redirecting (buttons disabled by the status) or reset to
      // initial on failure, so the status checks take over from here.
      requestInFlight.current = false;
    }
  }

  function onInvalidSubmit(errors: FieldErrors<MatcherVerificationStepFields>) {
    trackStepErrorViewed({
      step: activeStep + 1,
      step_name: STEP_NAME,
      step_label: STEP_LABEL,
      error_type: 'validation',
      error_field: 'verification_code',
      error_message: errors.verification_code?.message ?? '',
      form_name: getMatcherFormName()
    });
  }

  async function endVerification(phone: string, verificationCode: string) {
    const bypassVerificationQueryParam = getVerificationBypass();

    const payload =
      bypassVerificationQueryParam?.length > 0
        ? {
            phoneNumber: phone,
            verificationCode: verificationCode,
            bypassVerificationQuery: bypassVerificationQueryParam,
            postalCode
          }
        : {
            phoneNumber: phone,
            verificationCode: verificationCode
          };

    setStatus(MatcherVerificationStatus.verifyingCode);

    const responsePromise = await fetch(
      `${getTaxFullUrl()}/api/phone-validation-finish/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }
    );
    const verificationResponseBody = await responsePromise?.json();
    if (typeof verificationResponseBody?.validated !== 'boolean') {
      throw new Error('Invalid response from phone-validation-finish endpoint');
    }
    return verificationResponseBody;
  }

  return (
    <MatcherStepWrapper>
      <form
        ref={formRef}
        onSubmit={(event) => handleSubmit(onSubmit, onInvalidSubmit)(event)}
        noValidate
        className="flex-1 flex flex-col"
      >
        <MatcherStandardStepTitle>
          Enter verification code.
        </MatcherStandardStepTitle>
        <MatcherStandardStepSubtitleText>
          We sent a code to {formatPhoneForDisplay(phone)}.
        </MatcherStandardStepSubtitleText>
        <div className="space-y-2 mb-4">
          <FormField
            id="verification_code"
            label="Code"
            size={fieldSize}
            invalid={!!errors?.verification_code}
            errors={[errors?.verification_code?.message]}
          >
            {() => (
              <Input
                {...register('verification_code')}
                className={
                  '!w-full placeholder:text-gray-50 placeholder:text-3 sm:placeholder:text-4'
                }
                size={fieldSize}
                id="verification_code"
                maxLength={6}
                placeholder="000000"
                autoComplete="one-time-code"
                data-testid="tax_matcher_verification_code_input"
                invalid={!!errors?.verification_code}
                {...MATCHER_NUMERIC_INPUT_ATTRS}
              />
            )}
          </FormField>
        </div>

        {errorMessage && <MatcherErrorMessage errorMessage={errorMessage} />}
        {successMessage && (
          <MatcherSuccessMessage successMessage={successMessage} />
        )}

        <div className="mt-auto md:mt-0 flex flex-col gap-1 items-center">
          <MatcherSubmitButton status={status} />
          <MatcherResubmitButton status={status} onClick={handleResendCode} />
        </div>
      </form>
    </MatcherStepWrapper>
  );
}

export { MatcherVerificationStep };
